/**
 * InsightsTab - Display rule-based insights with severity and linked diagnostics
 */
import { FC, useState, useMemo } from 'react';
import { Card, CardContent, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@pagespeedify/ui';
import { AlertTriangle, Info, CheckCircle, ChevronDown, ChevronRight, TrendingUp, TrendingDown, Lightbulb } from 'lucide-react';
import { Insight, FlattenedMonitoringHistory } from '../../../types';

interface InsightsTabProps {
    insights: Insight[];
    history: FlattenedMonitoringHistory[];
    fieldDataHistory: any[]; // Field data from additional_metrics.field_data_history
}

const severityColors = {
    high: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
};

const typeConfig = {
    warning: { Icon: AlertTriangle, color: 'text-amber-500' },
    info: { Icon: Info, color: 'text-blue-500' },
    success: { Icon: CheckCircle, color: 'text-green-500' },
};

export const InsightsTab: FC<InsightsTabProps> = ({ insights, history, fieldDataHistory }) => {
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

    const toggleExpanded = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };



    // Extract field data insights from latest field scan (if available)
    const fieldDataInsights = useMemo(() => {
        if (!fieldDataHistory || fieldDataHistory.length === 0) return [];

        const latestFieldScan = fieldDataHistory[0]; // Most recent field scan
        const audits = latestFieldScan?.audits || {};
        const insights: any[] = [];

        // Extract bootup time with actual script URLs (not main thread work categories)
        if (audits['bootup-time']?.details?.items) {
            const items = audits['bootup-time'].details.items;
            const significantScripts = items.filter((item: any) => item.scripting && item.scripting > 100); // > 100ms scripting time

            if (significantScripts.length > 0) {
                const totalScriptingTime = significantScripts.reduce((sum: number, item: any) => sum + (item.scripting || 0), 0);

                // Filter for items with actual file URLs (not "Unattributable" or missing)
                const itemsWithUrls = significantScripts
                    .filter((item: any) => item.url && item.url.startsWith('http'))
                    .slice(0, 15)
                    .map((item: any) => ({
                        url: item.url,
                        duration: `Execution: ${(item.scripting || 0).toFixed(0)}ms`
                    }));

                // Build insight object
                const insight: any = {
                    id: 'field-bootup-time',
                    type: 'warning' as const,
                    severity: 'high' as const,
                    title: 'JavaScript Execution Time (Real Users)',
                    description: `${totalScriptingTime.toFixed(0)}ms spent executing JavaScript is blocking page interactivity for real users`,
                    metric: 'TBT',
                    source: 'field',
                };

                // Only add linkedDiagnostics if we have actual file URLs
                if (itemsWithUrls.length > 0) {
                    insight.linkedDiagnostics = {
                        type: 'JavaScript Files by Execution Time',
                        items: itemsWithUrls
                    };
                }

                insights.push(insight);
            }
        }

        // Extract render-blocking resources with URLs
        if (audits['render-blocking-resources']?.details?.items?.length > 0) {
            const items = audits['render-blocking-resources'].details.items;
            insights.push({
                id: 'field-render-blocking',
                type: 'warning' as const,
                severity: 'high' as const,
                title: 'Render-Blocking Resources (Real Users)',
                description: `${items.length} resources are blocking page render for real users`,
                metric: 'LCP',
                source: 'field',
                linkedDiagnostics: {
                    type: 'Blocking Resources',
                    items: items.slice(0, 15).map((item: any) => ({
                        url: item.url,
                        wastedMs: `${(item.wastedMs || 0).toFixed(0)}ms blocked`
                    }))
                }
            });
        }

        // Extract unused JavaScript with actual file URLs
        if (audits['unused-javascript']?.details?.items?.length > 0) {
            const items = audits['unused-javascript'].details.items;
            const totalWasted = items.reduce((sum: number, item: any) =>
                sum + (item.wastedBytes || 0), 0
            );
            if (totalWasted > 100000) { // Only show if > 100KB wasted
                insights.push({
                    id: 'field-unused-js',
                    type: 'warning' as const,
                    severity: 'medium' as const,
                    title: 'Unused JavaScript (Real Users)',
                    description: `${(totalWasted / 1024).toFixed(0)}KB of unused JavaScript is being downloaded by real users`,
                    metric: 'TBT',
                    source: 'field',
                    linkedDiagnostics: {
                        type: 'JavaScript Files',
                        items: items.slice(0, 15).map((item: any) => ({
                            url: item.url,
                            wastedBytes: `${(item.wastedBytes / 1024).toFixed(1)}KB unused`
                        }))
                    }
                });
            }
        }

        // Extract unused CSS with actual file URLs
        if (audits['unused-css-rules']?.details?.items?.length > 0) {
            const items = audits['unused-css-rules'].details.items;
            const totalWasted = items.reduce((sum: number, item: any) =>
                sum + (item.wastedBytes || 0), 0
            );
            if (totalWasted > 50000) { // Only show if > 50KB wasted
                insights.push({
                    id: 'field-unused-css',
                    type: 'warning' as const,
                    severity: 'medium' as const,
                    title: 'Unused CSS (Real Users)',
                    description: `${(totalWasted / 1024).toFixed(0)}KB of unused CSS is being downloaded by real users`,
                    metric: 'FCP',
                    source: 'field',
                    linkedDiagnostics: {
                        type: 'CSS Files',
                        items: items.slice(0, 10).map((item: any) => ({
                            url: item.url,
                            wastedBytes: `${(item.wastedBytes / 1024).toFixed(1)}KB unused`
                        }))
                    }
                });
            }
        }

        // Extract LCP element with actual element info
        if (audits['largest-contentful-paint-element']) {
            const lcpElement = audits['largest-contentful-paint-element'].details?.items?.[0];
            if (lcpElement) {
                insights.push({
                    id: 'field-lcp-element',
                    type: 'info' as const,
                    severity: 'low' as const,
                    title: 'Largest Contentful Paint Element (Real Users)',
                    description: `Real users see this element as LCP: ${lcpElement.node?.nodeLabel || 'element'}`,
                    metric: 'LCP',
                    source: 'field',
                    linkedDiagnostics: {
                        type: 'DOM Element',
                        items: [{
                            selector: lcpElement.node?.selector || 'N/A',
                            snippet: lcpElement.node?.snippet || lcpElement.node?.nodeLabel || 'N/A'
                        }]
                    }
                });
            }
        }
        return insights;
    }, [fieldDataHistory]);

    // Combine backend insights with field data insights
    // Clean up backend insights to remove linkedDiagnostics that don't have real file URLs (only categories)
    const cleanedBackendInsights = (insights || []).map((insight: any) => {
        // If insight has linkedDiagnostics, check if it has actual file URLs
        if (insight.linkedDiagnostics?.items) {
            const hasRealUrls = insight.linkedDiagnostics.items.some((item: any) =>
                item.url && typeof item.url === 'string' && item.url.startsWith('http')
            );

            // If no real URLs (only categories like "Script Evaluation"), remove linkedDiagnostics
            if (!hasRealUrls) {
                const { linkedDiagnostics, ...insightWithoutDiagnostics } = insight;
                return insightWithoutDiagnostics;
            }
        }

        return insight;
    });

    const allInsights = [...cleanedBackendInsights, ...fieldDataInsights];
    const hasAnyInsights = allInsights.length > 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Performance Insights</span>
                {hasAnyInsights && (
                    <span className="text-xs text-muted-foreground">({allInsights.length})</span>
                )}
            </div>



            {/* Empty State */}
            {!hasAnyInsights && (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-70" />
                        <p className="font-medium text-foreground">No insights available</p>
                        <p className="text-sm mt-1">
                            {history.length === 0
                                ? 'Start monitoring to generate insights.'
                                : 'All metrics are within acceptable thresholds.'
                            }
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Insights List */}
            {hasAnyInsights && (
                <div className="space-y-3">
                    {allInsights.map((insight: any) => {
                        const { Icon, color } = typeConfig[insight.type as keyof typeof typeConfig];
                        const isExpanded = expandedIds.has(insight.id);
                        const hasDetails = insight.linkedDiagnostics && insight.linkedDiagnostics.items.length > 0;

                        return (
                            <Collapsible
                                key={insight.id}
                                open={isExpanded}
                                onOpenChange={() => hasDetails && toggleExpanded(insight.id)}
                            >
                                <div className={`rounded-lg border p-4 transition-colors ${insight.type === 'success'
                                    ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800'
                                    : 'bg-card hover:bg-accent/5'
                                    }`}>
                                    <CollapsibleTrigger asChild disabled={!hasDetails}>
                                        <button
                                            className={`w-full text-left ${hasDetails ? 'cursor-pointer' : 'cursor-default'}`}
                                            type="button"
                                        >
                                            <div className="flex items-start gap-3">
                                                <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${color}`} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-medium">{insight.title}</span>
                                                        <span className={`px-1.5 py-0.5 rounded text-xs ${severityColors[insight.severity as keyof typeof severityColors]}`}>
                                                            {insight.severity}
                                                        </span>
                                                        {insight.metric !== 'general' && (
                                                            <span className="px-1.5 py-0.5 rounded text-xs bg-muted uppercase">
                                                                {insight.metric}
                                                            </span>
                                                        )}
                                                        {insight.source === 'field' && (
                                                            <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 font-medium">
                                                                Real Users
                                                            </span>
                                                        )}
                                                        {insight.trend && (
                                                            insight.trend === 'degrading'
                                                                ? <TrendingDown className="h-4 w-4 text-red-500" />
                                                                : insight.trend === 'improving'
                                                                    ? <TrendingUp className="h-4 w-4 text-green-500" />
                                                                    : null
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        {insight.description}
                                                    </p>
                                                </div>
                                                {hasDetails && (
                                                    <div className="flex-shrink-0">
                                                        {isExpanded
                                                            ? <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                                            : <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                                        }
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    </CollapsibleTrigger>

                                    {hasDetails && (
                                        <CollapsibleContent>
                                            <div className="mt-4 pt-4 border-t">
                                                <p className="text-sm font-medium mb-2 text-muted-foreground">
                                                    Related Resources ({insight.linkedDiagnostics?.type}):
                                                </p>
                                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                                    {insight.linkedDiagnostics?.items.map((item: any, idx: number) => (
                                                        <div
                                                            key={idx}
                                                            className="text-xs bg-muted rounded p-2 font-mono truncate"
                                                            title={item.url || item.selector || JSON.stringify(item)}
                                                        >
                                                            {item.url || item.selector || item.groupLabel || JSON.stringify(item).slice(0, 100)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </CollapsibleContent>
                                    )}
                                </div>
                            </Collapsible>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default InsightsTab;
